import { api } from '../../utils/api';

export const API_BASE_URL = '/api';

export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';

    // Custom messages are treated as warnings
    this.isCustomMessage = status === 429;
    this.customMessage = (() => {
      switch(status){
        case 429:
          return 'You are doing too many requests. Please try again later.';
        default:
          return message;
      }
    })();
  }

}

// Track ongoing refresh to prevent concurrent refresh attempts
let refreshPromise = null;

// Helper function to refresh the token
const refreshToken = async () => {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start a new refresh
  refreshPromise = (async () => {
    try {
      // Use the api utility to ensure proper cookie handling through Vite proxy
      const response = await api.post('/api/auth/refresh');

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      return response;
    } finally {
      // Clear the promise when done (success or failure)
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Wrap API calls with automatic retry on 401
const makeAuthenticatedRequest = async (requestFn, isRetry = false) => {
  let response = await requestFn();

  // If we get a 401, try to refresh and retry once (only if not already retrying)
  if (response.status === 401 && !isRetry) {
    const errorData = await response.clone().json().catch(() => ({}));

    // Only attempt refresh if backend indicates we should
    if (errorData.shouldRefresh === true) {

      try {
        // Call the refresh endpoint to get new cookies
        const refreshResponse = await refreshToken();

        // Ensure the refresh response is fully consumed to guarantee cookies are set
        await refreshResponse.json().catch(() => ({}));

        // Delay to ensure browser processes the new cookie
        await new Promise(resolve => setTimeout(resolve, 200));

        // Retry the original request with new cookies
        response = await requestFn();

        // If retry also fails with 401, something is wrong with the backend
        if (response.status === 401) {
          // Don't redirect here - just let the error be handled normally
          // The AuthContext will catch this and handle the unauthenticated state
        }
      } catch (refreshError) {
        // Refresh failed - redirect to login
        window.location.href = '/login';

        // Still reject to prevent further processing
        throw refreshError;
      }
    }
  }

  return handleResponse(response);
};

// Wrap the authenticated api with handleResponse and automatic token refresh
export const authenticatedApi = {
  get: async (url) => {
    return makeAuthenticatedRequest(() => api.get(url));
  },
  post: async (url, data) => {
    return makeAuthenticatedRequest(() => api.post(url, data));
  },
  put: async (url, data) => {
    return makeAuthenticatedRequest(() => api.put(url, data));
  },
  delete: async (url, data) => {
    return makeAuthenticatedRequest(() => api.delete(url, data));
  },
  patch: async (url, data) => {
    return makeAuthenticatedRequest(() => api.patch(url, data));
  },
};

export const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Note: Token expiration (401) is handled in makeAuthenticatedRequest
    // with automatic refresh and retry logic

    // Backend 4xx bodies use either `error` or `message` (ADR-195: the submit
    // guard's "required skills have not been assessed" is a `message`), so
    // honour both before falling back to the generic text.
    throw new ApiError(
      errorData.error || errorData.message || 'API request failed',
      response.status,
      errorData
    );
  }
  
  if (response.status === 204) {
    return null;
  }
  
  // Check content type to determine response handling
  const contentType = response.headers.get('Content-Type');
  if (contentType && (
        contentType.includes('image/')
        || contentType.includes('application/vnd.openxmlformats-officedocument')
        || contentType.includes('application/octet-stream')
        || contentType.includes('application/pdf')
      )) {
    return response; // Return the response directly for binary files (images, documents, etc.)
  }
  
  // For JSON and other data types
  return response.json();
};